import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import type { UserRoleDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Copy01Icon } from "@/components/Icons/Copy01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { Text } from "@/design-system/Text";
import { getRoleLabelByType } from "@/helpers/user-roles";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { permissionMutations } from "@/queries/permissions/mutations";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function RoleListPage(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const { componentProps, openDeleteModal } = useDeleteModal<string>("role-delete-modal");
  const navigate = useNavigate();
  const sessionUser = useSessionUser();

  const permissionQueries = usePermissionQueries();
  const { data: roles = [], isFetching: isFetchingRoles, error: errorRoles } = useQuery(permissionQueries.listRoles());
  const deleteRoleMutation = permissionMutations.useDeleteRole();

  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<UserRoleDto>();

    return [
      helper.accessor("name", {
        header: t("model.role.name"),
        cell: (cell) => cell.getValue(),
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("type", {
        header: t("model.role.type"),
        cell: (cell) => <Text tone="secondary">{getRoleLabelByType(t, cell.getValue())}</Text>,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              icon: <Edit05Icon />,
              value: "edit",
              callbackFn: () => {
                void navigate(routes.roles.edit({ slug, id: cell.getValue() }));
              },
              "data-testid": "context-menu-edit-btn",
              isHidden: cell.row.original.locked,
            },
            {
              labels: {
                default: t("common.action.duplicate"),
              },
              icon: <Copy01Icon />,
              value: "duplicate",
              callbackFn: () =>
                void navigate(routes.roles.create({ slug }), {
                  state: { roleId: cell.getValue() },
                }),
              "data-testid": "context-menu-duplicate-btn",
              isHidden: cell.row.original.locked,
            },
            {
              labels: {
                default: t("common.action.delete"),
              },
              icon: <Trash02Icon />,
              value: "delete",
              callbackFn: () => openDeleteModal(cell.getValue()),
              "data-testid": "context-menu-delete-btn",
              isHidden: cell.row.original.default,
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [t, slug, navigate, openDeleteModal]);

  const sortedRoles = useMemo(
    () =>
      roles.toSorted((a, b) => {
        // Residents first, then sort by name
        if (a.type === "resident" && b.type !== "resident") {
          return -1;
        }

        if (a.type !== "resident" && b.type === "resident") {
          return 1;
        }

        return a.name.localeCompare(b.name);
      }),
    [roles],
  );

  const tableInstance = useResizableTable(TABLE_KEYS.ROLES, {
    columns,
    data: sortedRoles,
    enableSorting: false,
  });

  if (errorRoles) {
    return <ErrorPage error={errorRoles} />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.role.list.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.role.list.title"),
            },
          ]}
        />
      }
      actions={
        <Button type="link" href={routes.roles.create({ slug })} data-testid="role-create-page-link">
          {t("model.role.action.create")}
        </Button>
      }
    >
      <Table
        table={tableInstance}
        data-testid="role-list"
        getRowHref={(row) =>
          routes.roles.details({
            id: row.original.id,
            slug,
          })
        }
        isLoading={isFetchingRoles}
      />
      <DeleteModal<string>
        title={t("model.role.action.delete.confirmation")}
        description={t("model.role.action.delete.description")}
        onDelete={deleteRoleMutation.mutateAsync}
        {...componentProps}
      />
    </DocumentPage>
  );
}
